import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import {
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material'
import React from 'react'

import { DoneState } from '../lib/useDoneState'
import { NotesState } from '../lib/useNotes'
import { TextEditsState } from '../lib/useTextEdits'
import { Voiceover } from '../types'

import { EditableRichText } from './EditableRichText'
import { NoteButton } from './NoteButton'
import { ReadingControls } from './ReadingControls'
import { ResizableReader } from './ResizableReader'

type Props = {
  rows: Voiceover[]
  index: number
  modeTitle: string
  fontSize: number
  onFontSize: (n: number) => void
  done: DoneState
  notes: NotesState
  textEdits: TextEditsState
  onIndexChange: (index: number) => void
}

export const VoiceoverDetail: React.FC<Props> = ({
  rows,
  index,
  modeTitle,
  fontSize,
  onFontSize,
  done,
  notes,
  textEdits,
  onIndexChange
}) => {
  const row = rows[index]
  if (!row) return null

  const isDone = done.isDone(row.suggestedFileName)

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        overflow: 'hidden'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<NavigateBeforeIcon />}
          disabled={index <= 0}
          onClick={() => onIndexChange(index - 1)}
        >
          Zurück
        </Button>
        <Typography variant="body2" color="text.secondary">
          {index + 1} / {rows.length}
        </Typography>
        <Button
          endIcon={<NavigateNextIcon />}
          disabled={index >= rows.length - 1}
          onClick={() => onIndexChange(index + 1)}
        >
          Weiter
        </Button>
      </Box>

      <Breadcrumbs>
        {modeTitle ? <Typography variant="body2">{modeTitle}</Typography> : null}
        <Typography variant="body2">{row.poiTitle}</Typography>
        <Typography variant="body2">{row.sectionTitle}</Typography>
        <Typography variant="body2">{row.nuggetTitle}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontFamily: 'monospace' }}>
          {row.suggestedFileName}
        </Typography>
        <Tooltip title="Dateiname kopieren">
          <IconButton
            size="small"
            onClick={() =>
              navigator.clipboard?.writeText(row.suggestedFileName)
            }
          >
            <ContentCopyIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
        <FormControlLabel
          sx={{ ml: 1 }}
          control={
            <Checkbox
              checked={isDone}
              onChange={() => done.toggle(row.suggestedFileName)}
            />
          }
          label="Erledigt"
        />
        <NoteButton
          note={notes.get(row.suggestedFileName)}
          onChange={(t) => notes.set(row.suggestedFileName, t)}
        />
      </Box>

      <Divider />

      <ReadingControls fontSize={fontSize} onFontSize={onFontSize} />

      {/* Drag the thick left/right edges to set the reading width. */}
      <ResizableReader contentSx={{ py: 3, opacity: isDone ? 0.55 : 1 }}>
        <EditableRichText
          key={row.suggestedFileName}
          original={row.text}
          value={textEdits.get(row.suggestedFileName)}
          onChange={(html) => textEdits.set(row.suggestedFileName, html)}
          fontSize={fontSize}
        />
      </ResizableReader>
    </Box>
  )
}
